import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { EmptyState } from "@/components/ui/empty-state";
import { Trash2, Pencil, Search, Filter, ChevronDown, Plus, Clock, ArrowUpDown, ArrowUp, ArrowDown, Check, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { TransactionEditDialog } from "./TransactionEditDialog";
import { motion, AnimatePresence } from "framer-motion";
import { useDeleteTransaction } from "@/hooks/mutations";
import { isToday, isYesterday, isThisWeek, isThisMonth, format } from "date-fns";
import { queryKeys } from "@/hooks/queries";
import type { Transaction } from "@/hooks/queries";
import { parseLocalDate } from "@/lib/dates";

interface TransactionListProps {
  transactions: Transaction[];
  onAddTransaction?: () => void;
}

type SortField = "date" | "amount" | "category" | "description";
type SortDirection = "asc" | "desc";

export const TransactionList = ({ transactions, onAddTransaction }: TransactionListProps) => {
  const { toast } = useToast();
  const { formatCurrency } = useUserPreferences();
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [displayCount, setDisplayCount] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "income" | "expense">("all");
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const deleteTransaction = useDeleteTransaction();
  const queryClient = useQueryClient();

  // The edit dialog saves on its own, so refresh what the save touched (as delete does).
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.budgets.all });
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setEditDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTransaction.mutateAsync(id);
      toast({
        title: "Success",
        description: "Transaction deleted",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };

  const availableCategories = useMemo(
    () =>
      Array.from(new Set(transactions.map((t) => t.category)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [transactions],
  );

  const sortedAndFilteredTransactions = useMemo(() => {
    const filtered = transactions.filter((t) => {
      const matchesSearch =
        t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.account?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        (t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      const matchesType = typeFilter === "all" || t.type === typeFilter;

      const matchesCategory =
        categoryFilters.length === 0 || categoryFilters.includes(t.category);

      return matchesSearch && matchesType && matchesCategory;
    });

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "date":
          comparison = a.date.localeCompare(b.date);
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
        case "description": {
          const aDesc = a.description || a.category;
          const bDesc = b.description || b.category;
          comparison = aDesc.localeCompare(bDesc);
          break;
        }
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [transactions, searchQuery, typeFilter, categoryFilters, sortField, sortDirection]);

  const displayedTransactions = sortedAndFilteredTransactions.slice(0, displayCount);
  const hasMore = displayCount < sortedAndFilteredTransactions.length;

  const formatDate = (dateString: string) => {
    return parseLocalDate(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDateGroup = (dateString: string): string => {
    const date = parseLocalDate(dateString);
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    if (isThisWeek(date)) return "This Week";
    if (isThisMonth(date)) return "This Month";
    return format(date, "MMMM yyyy");
  };

  // Group transactions by date
  const groupedTransactions = useMemo(() => {
    const groups: { [key: string]: typeof displayedTransactions } = {};
    displayedTransactions.forEach((tx) => {
      const group = getDateGroup(tx.date);
      if (!groups[group]) groups[group] = [];
      groups[group].push(tx);
    });
    return groups;
  }, [displayedTransactions]);

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <Button
      variant={sortField === field ? "secondary" : "ghost"}
      size="sm"
      className="h-7 text-xs gap-1"
      onClick={() => toggleSort(field)}
    >
      {label}
      {sortField === field ? (
        sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </Button>
  );

  return (
    <>
      <TransactionEditDialog
        transaction={editingTransaction}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
      />
      <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
        {/* Gradient top border on hover */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <CardHeader className="pb-3 pt-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold">Transactions</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="h-8 text-muted-foreground hover:text-foreground"
            >
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mb-3"
              >
                <div className="space-y-2 pb-3 border-b">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search transactions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-8 pl-8 text-sm"
                      />
                    </div>
                    <Select value={typeFilter} onValueChange={(v: any) => setTypeFilter(v)}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 justify-between text-sm font-normal"
                        >
                          <span className="truncate">
                            {categoryFilters.length === 0
                              ? "All categories"
                              : categoryFilters.length === 1
                                ? categoryFilters[0]
                                : `${categoryFilters.length} categories`}
                          </span>
                          <ChevronDown className="h-3.5 w-3.5 opacity-60 flex-shrink-0 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[240px]" align="start">
                        <Command>
                          <CommandInput placeholder="Search categories..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>No categories.</CommandEmpty>
                            <CommandGroup>
                              {availableCategories.map((category) => {
                                const selected = categoryFilters.includes(category);
                                return (
                                  <CommandItem
                                    key={category}
                                    onSelect={() => {
                                      setCategoryFilters((prev) =>
                                        selected
                                          ? prev.filter((c) => c !== category)
                                          : [...prev, category],
                                      );
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                                    />
                                    {category}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {categoryFilters.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {categoryFilters.map((cat) => (
                        <Badge
                          key={cat}
                          variant="secondary"
                          className="gap-1 cursor-pointer hover:bg-muted"
                          onClick={() =>
                            setCategoryFilters((prev) => prev.filter((c) => c !== cat))
                          }
                        >
                          {cat}
                          <X className="h-3 w-3" />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs text-muted-foreground mr-1">Sort:</span>
                    <SortButton field="date" label="Date" />
                    <SortButton field="amount" label="Amount" />
                    <SortButton field="category" label="Category" />
                    <SortButton field="description" label="Name" />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-2">
            {transactions.length === 0 ? (
              <EmptyState
                icon={Plus}
                title="No transactions yet"
                description="Start tracking your finances by adding your first transaction. You can add income, expenses, or import from a CSV file."
                action={onAddTransaction ? {
                  label: "Add Transaction",
                  onClick: onAddTransaction
                } : undefined}
              />
            ) : sortedAndFilteredTransactions.length === 0 ? (
              <EmptyState
                icon={Search}
                title="No matches found"
                description="No transactions match your current search or filter criteria. Try adjusting your filters or search terms."
                action={{
                  label: "Clear Filters",
                  onClick: () => {
                    setSearchQuery("");
                    setTypeFilter("all");
                    setCategoryFilters([]);
                  }
                }}
              />
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedTransactions).map(([group, txs]) => (
                  <div key={group}>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      {group}
                    </h4>
                    <AnimatePresence mode="popLayout">
                      <div className="space-y-1.5">
                        {txs.map((transaction, index) => (
                          <motion.div
                            key={transaction.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.15, delay: index * 0.02 }}
                            layout
                            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors duration-150 group/item"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                transaction.type === "income" ? "bg-income" : "bg-expense"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium truncate">
                                    {transaction.description || transaction.category}
                                  </span>
                                  {transaction.is_backdated && (
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <Clock className="h-3 w-3 text-amber-500" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Historical transaction</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {transaction.category}{transaction.account ? ` • ${transaction.account}` : ""}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span
                                className={`text-sm font-semibold font-mono-numbers whitespace-nowrap ${
                                  transaction.type === "income" ? "text-income-text" : "text-expense-text"
                                }`}
                              >
                                {transaction.type === "income" ? "+" : "-"}
                                {formatCurrency(Number(transaction.amount))}
                              </span>
                              <div className="flex gap-0.5 opacity-100 sm:opacity-0 sm:group-hover/item:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => handleEdit(transaction)}
                                >
                                  <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-primary-text" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => handleDelete(transaction.id)}
                                  disabled={deleteTransaction.isPending}
                                >
                                  {deleteTransaction.isPending && deleteTransaction.variables === transaction.id ? (
                                    <div className="h-3.5 w-3.5 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive transition-colors" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
            
            {hasMore && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="pt-2"
              >
                <Button
                  variant="outline"
                  className="w-full h-9 text-sm"
                  onClick={() => setDisplayCount(prev => prev + 10)}
                >
                  <ChevronDown className="h-4 w-4 mr-1.5" />
                  Load More ({sortedAndFilteredTransactions.length - displayCount} remaining)
                </Button>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
